import { Controller } from "@hotwired/stimulus";

type TranscriptionState =
  | "idle"
  | "requesting"
  | "listening"
  | "transcribing"
  | "discarding"
  | "ready"
  | "empty"
  | "failed";

const TRANSCRIPTION_STATES = new Set<TranscriptionState>([
  "idle",
  "requesting",
  "listening",
  "transcribing",
  "discarding",
  "ready",
  "empty",
  "failed",
]);

/**
 * Case-note dictation UI adapter.
 *
 * Audio capture and transport live outside this controller. The adapter emits
 * speech-dictation:* commands and consumes speech-transcription:* results on
 * the form, leaving the existing case-note form responsible for persistence.
 */
export default class extends Controller<HTMLFormElement> {
  static targets = ["input", "toggle", "status", "mirror", "submit"];

  declare inputTarget: HTMLTextAreaElement;
  declare toggleTarget: HTMLButtonElement;
  declare statusTarget: HTMLElement;
  declare hasMirrorTarget: boolean;
  declare mirrorTarget: HTMLElement;
  declare submitTargets: HTMLButtonElement[];

  private state: TranscriptionState = "idle";
  private nextSequence = 0;
  private pendingSegments = new Map<number, string>();
  private insertionCaret: number | null = null;
  private knownValue = "";
  private dispatchingInput = false;
  private insertedSpeech = false;

  connect() {
    this.knownValue = this.inputTarget.value;
    this.render();
  }

  toggle() {
    if (this.state === "requesting" || this.state === "listening") {
      this.stop();
      return;
    }

    this.start();
  }

  start() {
    if (["transcribing", "discarding"].includes(this.state)) return;

    this.pendingSegments.clear();
    this.nextSequence = 0;
    this.insertedSpeech = false;
    this.insertionCaret = this.inputTarget.selectionStart ?? this.inputTarget.value.length;
    this.setState("requesting");
    this.dispatch("start");
  }

  stop() {
    if (this.state !== "requesting" && this.state !== "listening") return;

    this.setState("transcribing");
    this.dispatch("stop");
  }

  stateChanged(event: Event) {
    const detail = (event as CustomEvent<{ state?: string }>).detail;
    const state = detail?.state;
    if (!state || !TRANSCRIPTION_STATES.has(state as TranscriptionState)) return;

    const nextState: TranscriptionState =
      state === "ready" && !this.insertedSpeech ? "empty" : (state as TranscriptionState);
    this.setState(nextState);
  }

  segmentReceived(event: Event) {
    const detail = (event as CustomEvent<{ sequence?: number; text?: string }>).detail;
    const sequence = detail?.sequence;

    if (!Number.isInteger(sequence) || (sequence as number) < this.nextSequence) return;
    if (this.pendingSegments.has(sequence as number)) return;

    this.pendingSegments.set(sequence as number, String(detail?.text || ""));
    this.insertReadySegments();
  }

  inputChanged() {
    const value = this.inputTarget.value;
    if (!this.dispatchingInput && this.insertionCaret !== null) {
      this.insertionCaret = this.adjustedCaret(this.knownValue, value, this.insertionCaret);
    }
    this.knownValue = value;
    this.syncMirror();
  }

  guardSubmit(event: Event) {
    if (!this.isBusy) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showStatus(this.element.dataset.submitBlockedLabel || "");
  }

  private setState(state: TranscriptionState) {
    this.state = state;
    this.render();
  }

  private get isBusy() {
    return ["requesting", "listening", "transcribing", "discarding"].includes(this.state);
  }

  private insertReadySegments() {
    while (this.pendingSegments.has(this.nextSequence)) {
      const text = this.pendingSegments.get(this.nextSequence) || "";
      this.pendingSegments.delete(this.nextSequence);
      this.nextSequence += 1;

      const normalized = text.trim();
      if (normalized) {
        this.insertedSpeech = true;
        this.insertAtCaret(normalized);
      }
    }
  }

  private insertAtCaret(text: string) {
    const input = this.inputTarget;
    const start = this.insertionCaret ?? input.selectionStart ?? input.value.length;
    const before = input.value.slice(0, start);
    const after = input.value.slice(start);
    const prefix = before && !/\s$/.test(before) ? " " : "";
    const suffix = after && !/^\s/.test(after) ? " " : "";
    const insertion = `${prefix}${text}${suffix}`;

    // Collapse a selection instead of replacing it: dictated text must never
    // erase text the staff member typed or selected.
    const userSelectionStart = input.selectionStart ?? start;
    const userSelectionEnd = input.selectionEnd ?? userSelectionStart;
    const selectionMode =
      userSelectionStart === start && userSelectionEnd === start ? "end" : "preserve";
    input.setRangeText(insertion, start, start, selectionMode);
    this.insertionCaret = start + insertion.length;
    this.dispatchInput();
    this.render();
  }

  private dispatchInput() {
    this.dispatchingInput = true;
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.dispatchingInput = false;
    this.knownValue = this.inputTarget.value;
  }

  private adjustedCaret(before: string, after: string, caret: number) {
    let prefix = 0;
    while (prefix < before.length && prefix < after.length && before[prefix] === after[prefix]) {
      prefix += 1;
    }

    let suffix = 0;
    while (
      suffix < before.length - prefix &&
      suffix < after.length - prefix &&
      before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
    ) {
      suffix += 1;
    }

    const oldEnd = before.length - suffix;
    const newEnd = after.length - suffix;
    if (caret <= prefix) return caret;
    if (caret >= oldEnd) return caret + (newEnd - oldEnd);
    return newEnd;
  }

  // One control, three faces: the microphone to start, a pause to stop, and
  // the spinner while the recording is being transcribed. The classes drive
  // which face shows (the ERB owns the icons); the label is the accessible
  // name, since the button carries no words.
  private render() {
    const recording = this.state === "requesting" || this.state === "listening";
    const busy = this.state === "transcribing" || this.state === "discarding";
    const label = recording
      ? this.toggleTarget.dataset.stopLabel
      : this.toggleTarget.dataset.dictateLabel;

    // The wave belongs to the moment it is hearing, not to the composer.
    this.element.classList.toggle("is-dictating", recording);
    this.toggleTarget.classList.toggle("is-recording", recording);
    this.toggleTarget.classList.toggle("is-busy", busy);
    this.toggleTarget.disabled = busy;
    this.toggleTarget.setAttribute("aria-pressed", String(recording));
    this.toggleTarget.setAttribute("aria-label", label || "");
    this.toggleTarget.title = label || "";

    // What it is doing is for assistive tech only — the faces and the wave
    // say it on screen — except a failure, which has to be readable.
    const message = this.statusTarget.dataset[this.state] || "";
    if (this.state === "failed" || this.state === "empty") this.showStatus(message);
    else this.hideStatus(message);

    this.syncMirror();
    this.element.dataset.speechDictationBusy = String(this.isBusy);

    const inputEmpty = this.inputTarget.value.length === 0;
    this.submitTargets.forEach((button) => {
      button.disabled = this.isBusy || inputEmpty;
    });
  }

  // The note is behind the dictation surface, so the words it is filling in
  // show on the surface itself.
  private syncMirror() {
    if (!this.hasMirrorTarget) return;

    const text = this.inputTarget.value.trim();
    this.mirrorTarget.textContent = text || this.mirrorTarget.dataset.emptyLabel || "";
    this.mirrorTarget.classList.toggle("text-slate-400", text.length === 0);
  }

  private showStatus(message: string) {
    this.statusTarget.textContent = message;
    this.statusTarget.classList.remove("sr-only");
  }

  private hideStatus(message: string) {
    this.statusTarget.textContent = message;
    this.statusTarget.classList.add("sr-only");
  }
}
